<script setup lang="ts">
import {useRoute, useRouter} from "vue-router";
import { computed, watch, watchEffect } from 'vue'
import { useModyApi } from "~/middelware/ModyApi";
import { Product } from "~/utils/Product";
import {useAsyncData} from "#app";
import type { MenuItem } from "primevue/menuitem";
import {Category} from "~/utils/Category";
import {useUserDataStorage} from "~/store/userdata";
import {useMyFunctions} from "~/middelware/MyFunctions";
import {useToast} from "primevue/usetoast";
import Galleria from "primevue/galleria";
import { Article, CartProduct } from "#imports";
import { addToCart, Cart } from "~/utils/Cart";
import type {AutoCompleteOptionSelectEvent} from "primevue/autocomplete";
import type { FileUploadSelectEvent } from "primevue/fileupload";
import type { DataTableRowSelectEvent } from "primevue/datatable";
import { useUrlSearchParams } from "@vueuse/core";

const route = useRoute();
const confirm = useConfirm()
const editMode = ref(false)
const userDataStore = useUserDataStorage();
const router = useRouter();
const api = useModyApi();
const amount = ref(1);
const productImageLoaded = ref(false)
const accordionOpen = ref<string>("0")
const toast = useToast()
const { toHtml,toText } = useEditorJsHtml()
const safeHtml = ref("")
const categories = ref<Category[]>([])
const newProduct = ref(new Product())
const autocompleteProductList = ref<Product[]>([])
const urlParams = useUrlSearchParams()


definePageMeta({
        middleware: "legacy-product-redirect"
    }
)

let editorContent = {}
const homeMenuItem = ref<MenuItem>({
  icon:"pi pi-home",
  route: "/shop"
})

const isProductPage = computed(() => route.name === "shop-product-slug")
const breadCrumbMenuItems = ref<MenuItem[]>([])

const productSlug = computed(() => route.params.slug as string)
watch(productSlug, () => {
  productImageLoaded.value = false
})
const { data:product, pending, refresh } = await useAsyncData(
  `product-${productSlug.value}`,
  async () => {
    if(productSlug.value == "new") return new Product().toJSON()
    try {
      const p = await api.getProductBySlug(productSlug.value)
      // if(p.id == -1 && isProductPage){
      //   navigateTo("/shop")
      // }
      return p.toJSON()
    } catch (error:any) {
      console.log(error)
    }
  },
  { watch: [productSlug]}
)

const error = useError()

watchEffect(() => {
  breadCrumbMenuItems.value = []
  if (product.value?.categories?.length) {
    addBreadcrumbForCategory(product.value?.categories[0]!)
  }
  safeHtml.value = product.value?.jsonDescription ? toHtml(product.value!.jsonDescription) : ""
  if(productSlug.value == "new" || urlParams.edit == "1"){
    edit()
  }
})


async function loadCategories(){
  categories.value = await api.getAllCategories()
}

function addBreadcrumbForCategory(category:Category){
  breadCrumbMenuItems.value.unshift({
    label:category.name,
    route:"/shop/category/"+category.slug
  })
  if(category.parentCategory){
    addBreadcrumbForCategory(category.parentCategory)
  }
}

useSeoMeta({
  title: product.value?.Name,
  description: product.value?.jsonDescription ? (Object.entries(product.value.jsonDescription) ? toText(product.value?.jsonDescription) : product.value?.Description) : product.value?.Description,
  ogTitle: "Mody Veranstaltungstechnik | " + product.value?.Name,
  ogDescription: product.value?.jsonDescription ? (Object.entries(product.value.jsonDescription) ? toText(product.value?.jsonDescription) : product.value?.Description) : product.value?.Description,
  ogImage: product.value?.images[0] && product.value?.images[0].id >= 0 ? product.value?.images[0].url : null,
  twitterCard: 'summary_large_image',
})
useHead({
  script: [
    {
      type: 'application/ld+json',
      innerHTML: {
        "@context": "https://schema.org/",
        "@type": "Product",
        "name": product.value?.Name,
        "image": product.value?.images[0] && product.value?.images[0].id >= 0 ? product.value?.images[0].url : null,
        "description": product.value?.Description,
        "aggregateRating": {
          "@type": "AggregateRating",
          "ratingValue": "4.5",
          "ratingCount": "6"
        },
        "offers": product.value?.Price && product.value?.Price > 0 ? {
          "@type": "AggregateOffer",
          "availability": "https://schema.org/InStock",
          "priceCurrency": "EUR",
          "lowPrice": product.value?.Price,
          "checkoutPageURLTemplate":"https://mody.at/shop/request?productToAdd="+product.value.id,
        } : undefined,
      }
    }
  ]
})

async function edit(){
  await loadCategories()
  editMode.value = true
  editorContent = JSON.parse(JSON.stringify(product.value?.jsonDescription))
}
async function save(){
  editMode.value = false

  if(!product.value) {
    alert("Fehler: Kein Artikel geladen")
    return
  }
  product.value.jsonDescription = editorContent
  const tempProduct = new Product().fromJSON(product.value!)
  try {
    if(product.value.id < 0){
        const response = await api.create("products/",tempProduct.toApiJSON())
        router.push("/shop/product/"+response.data.data.attributes.slug)
    }
    else{
        await api.update("products/"+product.value.id, tempProduct.toApiJSON())
    }
  } catch (error) {
    console.log(error)
  }
  safeHtml.value = product.value?.jsonDescription ? toHtml(product.value!.jsonDescription) : ""
  accordionOpen.value = "0"
  refresh()
}


async function remove(){
    await api.remove("products/"+product.value!.id)
    router.go(-1)
}


async function onSelect(event:FileUploadSelectEvent){
  //Get all files
  const newFile = (event.files as File[])[0]
  let response = {id:-1, url:""}
  if(newFile){
    response = await api.uploadFile(newFile)
  }
  // Reassign to trigger update when using shallow refs
  product.value = { ...(product.value as any), images: [ { ...response, alt: "" }] } as any
}


const confirmRemoval = () => {
  confirm.require({
    message: 'Soll der Artikel wirklich entfernt werden?',
    header: 'ACHTUNG',
    icon: 'pi pi-exclamation-triangle',
    acceptClass: 'p-button-danger',
    rejectLabel: 'Abbrechen',
    acceptLabel: 'Entfernen',
    accept: () => {
      remove()
    }
  });
};


/*
* ARTICLE FUNCTIONS
*/
const showNewArticleRow = ref(false)
const newArticleNumber = ref("")
function openArticle(event: DataTableRowSelectEvent){
  router.push("/admin/articleDetails?articleId="+event.data.id)
}


function addNewArticle(){
  showNewArticleRow.value = true
}
function cancelAddNewArticle(){
  showNewArticleRow.value = false
  newArticleNumber.value = ""
}
async function saveNewArticle(){
  const newArticle = new Article()
  newArticle.article_number = newArticleNumber.value
  newArticle.product = new Product().fromJSON(product.value!)
  const current = product.value?.articles || []
  product.value = { ...(product.value as any), articles: [...current, newArticle] } as any
  try{
    await api.create("articles",newArticle.toJSON())
    newArticleNumber.value = ""
    showNewArticleRow.value = false
  }catch (e:any){
    console.log(e)
  }
}
async function removeArticle(article:Article){
  try{
    await api.remove("articles/"+article.id)
    const current = product.value?.articles || []
    product.value = { ...(product.value as any), articles: current.filter(a=>a.id != article.id) } as any
  }catch (e:any){
    console.log(e)
  }
}

/* 
+ CHILD PRODUCT FUNCTIONS
*/
async function loadAutocompleteProducts(){
  autocompleteProductList.value = await api.searchProductsByName(newProduct.value.Name)
}

function setProduct(event: AutoCompleteOptionSelectEvent){
  const newChildProduct = new CartProduct(1,event.value,1,0,"","")
  const current = product.value?.childProducts || []
  product.value = { ...(product.value as any), childProducts: [...current, newChildProduct] } as any
  newProduct.value = new Product()
}
function childProductChanged(index:number,childProcduct:CartProduct){
  const oldChildProducts = product.value?.childProducts
  oldChildProducts![index] = childProcduct
  product.value = { ...(product.value as any), childProducts: oldChildProducts } as any
}
function removeChildProduct(index:number){
  const oldChildProducts = product.value?.childProducts
  oldChildProducts!.splice(index,1)
  product.value = { ...(product.value as any), childProducts: oldChildProducts } as any 
}

const childProductsTotal = computed(() => {
  return (product.value?.childProducts || []).reduce((sum, childProduct) => {
    return sum + Number(childProduct.product?.Price || 0) * Number(childProduct.count || 0)
  }, 0)
})

const productContext = computed(() => product.value?.categories?.[0]?.name || "Mietsortiment")
const hasRelatedProducts = computed(() => Boolean(product.value?.related_products?.length))

</script>
<template>
  <ConfirmDialog></ConfirmDialog>
  <Toast>
    <template #message="slotProps">
      <div style="width:100%">
        {{slotProps.message.detail}}
        <div @click.stop="router.push('/shop/request')" style="text-decoration: underline;cursor:pointer">
          zur Anfrage
        </div>
      </div>
    </template>
  </Toast>
  <main class="product-detail-page">
    <div class="product-detail-shell">
      <Breadcrumb class="product-breadcrumb" :home="homeMenuItem" :model="breadCrumbMenuItems" >
        <template #item="{ item, props }">
          <router-link v-if="item.route" v-slot="{ href, navigate }" :to="item.route" custom>
            <a :href="href" v-bind="props.action" @click="navigate">
              <span :class="[item.icon, 'text-color']" />
              <span class="font-semibold">{{ item.label }}</span>
            </a>
          </router-link>
          <a v-else :href="item.url" :target="item.target" v-bind="props.action">
            <span class="text-color">{{ item.label }}</span>
          </a>
        </template>
      </Breadcrumb>
      <div class="product" v-if="product && isProductPage">
        <section class="product-hero" aria-labelledby="product-title">
          <div class="product-gallery" v-if="!editMode" :aria-busy="!productImageLoaded">
            <Skeleton v-if="!productImageLoaded" class="product-gallery-skeleton" aria-hidden="true" />
            <Galleria :value="product?.images"
                      :showThumbnails="false" :showIndicators="product?.images.length > 1">
              <template #item="slotProps">
                <img
                  class="product-image"
                  :class="{ 'product-image--cover': !product?.transparentImage }"
                  v-if="product?.images"
                  :src="slotProps.item.url"
                  :alt="slotProps.item.alt || product.Name"
                  @load="productImageLoaded = true"
                  @error="productImageLoaded = true"
                />
              </template>
            </Galleria>
          </div>

          <div v-if="editMode" class="product-gallery product-gallery--edit">
              <Image v-if="product.images[0]" :src="product.images[0].url" height="50px"></Image>
              <FileUpload class="mt-2 p-button-outlined" customUpload mode="basic" :choose-label="product?.images[0]?'ändern':'Bild auswählen'" @select="onSelect"></FileUpload>
          </div>
          <div class="product-summary">
            <p v-if="!editMode" class="product-context">{{ productContext }}</p>
            <h1 v-if="!editMode" id="product-title" class="name">{{ product?.Name }}</h1>
            <InputText v-else v-model="product.Name"></InputText>

            <div v-if="!editMode && product?.badges?.length" class="product-badges" aria-label="Produkteigenschaften">
              <span v-for="badge in product.badges" :key="badge.label" :class="`product-badge product-badge--${badge.color}`">{{ badge.label }}</span>
            </div>

            <div class="offer-card">
              <div class="offer-price">
                <div class="fromPrice" v-if="!editMode && product?.setPrice && product?.setPrice > product?.Price">
                  <span class="currency">statt {{ product?.setPrice.toLocaleString("de-AT", {style: "currency", currency: "EUR"}) }}</span>
                  <span class="percent">{{ ((1-(product?.Price/product?.setPrice)) * 100).toFixed(0)}} % günstiger</span>
                </div>
                <p class="price-label" v-if="!editMode">Mietpreis</p>
                <div class="price">
              <template v-if="!editMode">
                <template v-if="product?.Price != 0">{{ product?.Price.toLocaleString("de-AT", {style: "currency", currency: "EUR"}) }}</template>
                <template v-else>Preis auf Anfrage</template>
              </template>
              <template v-else>
                <InputNumber v-model="product.Price" :min="0" mode="currency" currency="EUR"  locale="de-DE"></InputNumber>
              </template>
                </div>
              </div>
              <div v-if="!editMode" class="add-to-cart">
                <div class="amount-field">
                  <label for="product-amount">Anzahl</label>
                  <InputNumber
                    @click.stop
                    v-model="amount"
                    inputId="product-amount"
                    showButtons
                    buttonLayout="horizontal"
                    :min="1"
                    :pt="{
                      incrementButton: { 'aria-label': 'Anzahl erhöhen' },
                      decrementButton: { 'aria-label': 'Anzahl verringern' },
                    }"
                  />
                </div>
                <Button class="request-button" label="Zur Anfrage hinzufügen" icon="pi pi-plus" @click.stop="addToCart(amount, product as any, toast)" />
              </div>
            </div>

            <ul v-if="!editMode" class="request-benefits" aria-label="Vorteile der Anfrage">
              <li><i class="pi pi-check" aria-hidden="true"></i><span>Unverbindliche Anfrage</span></li>
              <li><i class="pi pi-comments" aria-hidden="true"></i><span>Persönliche Abstimmung</span></li>
              <li><i class="pi pi-shield" aria-hidden="true"></i><span>Keine automatische Buchung</span></li>
            </ul>

            <details v-if="!editMode" class="category-disclosure">
              <summary>
                <span><i class="pi pi-sitemap" aria-hidden="true"></i> Weitere Kategorien</span>
                <i class="pi pi-chevron-down disclosure-chevron" aria-hidden="true"></i>
              </summary>
              <div class="category-tree-content">
                <ShopSideMenu :show-title="false" />
              </div>
            </details>
          </div>
        </section>
        <Accordion class="details" v-model:value="accordionOpen">
            <AccordionPanel value="0" v-if="product?.Description || product?.jsonDescription">
              <AccordionHeader>Details</AccordionHeader>
              <AccordionContent>
                <ClientOnly>
                  <p v-if="!editMode && Object.keys(product?.jsonDescription).length == 0" v-html="product?.Description"></p>
                  <div v-if="!editMode && Object.keys(product?.jsonDescription).length > 0" v-html="safeHtml" class="mb-4 blocksEditor"/>
                </ClientOnly>
                <LazyBlocksEditor class="blocksEditor" v-if="product.jsonDescription && editMode" v-model="editorContent"></LazyBlocksEditor>
              </AccordionContent>
            </AccordionPanel>
            <AccordionPanel value="1" v-if="product?.manualUrl">
              <AccordionHeader>Downloads</AccordionHeader>
              <AccordionContent>
                <Button>
                  <a :href="product?.manualUrl" target="_blank">Bedienungsanleitung</a>
                </Button>
              </AccordionContent>
            </AccordionPanel>
            <AccordionPanel value="3" v-if="editMode">
              <AccordionHeader>Einstellungen</AccordionHeader>
              <AccordionContent>
                <div class="flex flex-col gap-2 mb-3">
                  <label>Type</label>
                  <SelectButton v-model="product.type" :options="['rental','sale']"></SelectButton>
                </div>
                <div class="flex flex-col gap-2 mb-3">
                  <label>Preis Sichtbarkeit</label>
                  <SelectButton v-model="product.showPrice" :options="['admin','customer','public']"></SelectButton>
                </div>
                <div class="flex gap-2 mb-3">
                  <div class="flex flex-col gap-2">
                    <label>Transparentes Bild</label>
                    <ToggleButton v-model="product.transparentImage" class="" :on-label="'Ja'" :off-label="'Nein'"></ToggleButton>
                  </div>
                  <div class="flex flex-col gap-2">
                    <label>Öffentlich sichtbar</label>
                    <ToggleButton v-model="product.Public" class="" :on-label="'Ja'" :off-label="'Nein'"></ToggleButton>
                  </div>
                </div>

                <div class="flex flex-col gap-2 mb-3">
                  <label>Kategorie</label>
                  <MultiSelect v-model="product.categories" dataKey="id" filter optionLabel="name" :options="categories" placeholder="Select Cagtegories" style="margin:0;max-width: 250px;"/>
                </div>
              </AccordionContent>
            </AccordionPanel>
            <AccordionPanel value="childProducts" v-if="editMode">
              <AccordionHeader>Sub-Produkte (Set)</AccordionHeader>
              <AccordionContent>
                <div>
                  <div v-for="(childProduct,index) in product.childProducts" class="border-y py-4">
                    <div class="flex justify-between items-center">
                      <div class="mb-2 text-lg">{{ childProduct.product.Name }}</div>
                      <Button icon="pi pi-trash" severity="danger" outlined rounded @click="removeChildProduct(index)"></Button>
                    </div>
                    <div class="flex justify-between items-center gap-4">
                      <div class="flex items-center gap-3 min-w-0">
                        <div>Anzahl</div>
                        <InputNumber inputClass="w-[92px] min-w-[92px] text-left" size="small" @update:modelValue="childProductChanged(index,childProduct)" buttonLayout="stacked" showButtons v-model="childProduct.count" :min="0" :step="0.1" :minFractionDigits="0" :maxFractionDigits="2" locale="de-AT"></InputNumber>
                      </div>
                      <div class="text-right whitespace-nowrap">{{(childProduct.product.Price * childProduct.count).toLocaleString("de-AT", {style:"currency",currency:"EUR"}) }}</div>
                    </div>
                  </div>
                  <div v-if="product.childProducts.length > 0" class="flex justify-between items-center py-4 font-bold text-lg border-t">
                    <div>Gesamtsumme</div>
                    <div>{{ childProductsTotal.toLocaleString("de-AT", {style:"currency",currency:"EUR"}) }}</div>
                  </div>
                  <div>
                    <div>Add Product</div>
                    <AutoComplete v-model="newProduct.Name" @complete="loadAutocompleteProducts()" :suggestions="autocompleteProductList" optionLabel="Name" @item-select="setProduct"/>
                  </div>
                </div>
              </AccordionContent>
            </AccordionPanel>
            <AccordionPanel value="articles" v-if="editMode">
              <AccordionHeader>Artikel</AccordionHeader>
              <AccordionContent>
                <DataTable :value="product.articles" selectionMode="single" @rowSelect="openArticle($event)">
                  <Column field="article_number" header="Artikelnummer"></Column>
                  <Column header="Aktionen" style="width:150px">
                    <template #body="slotProps">
                      <Button severity="danger" outlined @click="removeArticle(slotProps.data)">Entfernen</Button>
                    </template>
                  </Column>
                  <template #footer>
                    <Button v-if="!showNewArticleRow" @click="addNewArticle()" outlined>hinzufügen</Button>
                    <div v-else style="display: flex;align-items:center;">
                      <input-text v-model="newArticleNumber" style="margin: 0 10px 0 0;"></input-text>
                      <Button @click="saveNewArticle()" style="margin: 0 10px 0 0;">Speichern</Button>
                      <Button @click="cancelAddNewArticle()" severity="danger" outlined>Abbrechen</Button>
                    </div>
                  </template>
                </DataTable>
              </AccordionContent>
            </AccordionPanel>
          </Accordion>
      </div>
      <div v-else class="product-skeleton">
        <Skeleton height="15rem" style="margin-bottom:20px"></Skeleton>
        <Skeleton height="2rem" width="15rem" style="margin-bottom:20px"></Skeleton>
        <Skeleton height="1rem" width="10rem" style="margin-bottom:20px"></Skeleton>
      </div>
      <div v-if="userDataStore.loggedIn" class="container mb-2 flex justify-end flex-wrap gap-2 p-3">
          <Button v-if="!editMode" class="px-2 py-1 rounded" @click="edit()">Bearbeiten</Button>
          <Button v-if="editMode" class="px-2 py-1 rounded" @click="editMode = false">Abbrechen</Button>
          <Button v-if="editMode" class="px-2 py-1 rounded" @click="save()">Speichern</Button>
          <Button v-if="editMode && product?.id != -1" class="px-2 py-1 rounded" severity="danger" @click="confirmRemoval">Entfernen</Button>
      </div>
      <section class="product-consultation">
        <div>
          <h2>Wir planen die passende Lösung mit dir.</h2>
          <p>Lege das Produkt in deine Anfrage oder beschreibe dein Event direkt. Wir prüfen Verfügbarkeit und Zusammenstellung persönlich.</p>
        </div>
        <Button label="Anfrage öffnen" icon="pi pi-arrow-right" iconPos="right" @click="router.push('/shop/request')" />
      </section>
      <section v-if="hasRelatedProducts" class="related-products">
        <div class="related-heading">
          <h2>Weitere Produkte</h2>
        </div>
        <div class="related-products__list">
          <LazyShopRelatedProductItem v-for="related_product in product?.related_products" :product="related_product"></LazyShopRelatedProductItem>
        </div>
      </section>
    </div>
  </main>
</template>
<style scoped lang="scss">
.product-detail-page{
  min-height: 100vh;
  padding-bottom: clamp(4rem, 8vw, 7rem);
  color: white;
  background: $public-bg;
}

.product-detail-shell{
  width: min(100%, 1120px);
  margin: 0 auto;
  padding: 0 1.5rem;
}

.product-breadcrumb{
  padding: 1.35rem 0;
  border: 0;
  background: none;

  :deep(.p-breadcrumb-list){
    flex-wrap: wrap;
  }

  :deep(.p-breadcrumb-item-link){
    color: rgb(168 162 158);
    font-size: 0.86rem;
  }

  :deep(.p-breadcrumb-item-link:hover){
    color: white;
  }
}

.product-hero{
  display: grid;
  grid-template-columns: minmax(0, 1.12fr) minmax(340px, 0.88fr);
  gap: clamp(2rem, 4vw, 4rem);
  align-items: start;
  padding: clamp(0.75rem, 2vw, 1.75rem) 0 clamp(2.5rem, 5vw, 4.25rem);
}

.product-gallery{
  position: relative;
  min-width: 0;
  overflow: hidden;
  border: 1px solid rgba(255, 255, 255, 0.09);
  border-radius: $public-radius-large;
  background: $public-surface;

  .product-gallery-skeleton{
    position: absolute;
    inset: 0;
    width: 100% !important;
    height: 100% !important;
    border-radius: 0 !important;
  }

  :deep(.p-galleria){
    position: relative;
    z-index: 1;
    border: 0;
    background: transparent;
  }

  :deep(.p-galleria-content),
  :deep(.p-galleria-items-container){
    background: transparent;
  }

  .product-image{
    display: block;
    width: 100%;
    height: clamp(360px, 45vw, 590px);
    padding: clamp(1.25rem, 3vw, 2.5rem);
    object-fit: contain;
    background: $imgBackground;

    &.product-image--cover{
      height: auto;
      aspect-ratio: 16 / 10;
      padding: 0;
      object-fit: contain;
      background: $public-surface;
    }
  }

  :deep(.p-galleria-indicator-button){
    background: rgba(255, 255, 255, 0.4);
  }

  :deep(.p-galleria-indicator-active .p-galleria-indicator-button){
    background: #E39737;
  }
}

.product-gallery--edit{
  min-height: 250px;
  padding: 2rem;
}

.product-summary{
  padding-top: clamp(0.25rem, 2vw, 1.5rem);
}

.product-context{
  margin: 0 0 0.65rem;
  color: $public-text-muted;
  font-size: 0.82rem;
  font-weight: 600;
}

.name{
  max-width: 18ch;
  margin: 0;
  font-size: clamp(2.3rem, 4.3vw, 3.8rem);
  font-weight: 600;
  line-height: 1;
  letter-spacing: -0.025em;
  text-wrap: balance;
}

.product-badges{
  display: flex;
  flex-wrap: wrap;
  gap: 0.55rem;
  margin-top: 1.4rem;
}

.product-badge{
  padding: 0.45rem 0.72rem;
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 999px;
  color: rgb(231 229 228);
  background: rgba(255, 255, 255, 0.06);
  font-size: 0.78rem;
  font-weight: 650;
}

.product-badge--pax{
  border-color: rgba(80, 157, 210, 0.38);
  background: rgba(62, 127, 173, 0.2);
}

.product-badge--feature{
  border-color: rgba(227, 151, 55, 0.38);
  background: rgba(227, 151, 55, 0.14);
}

.offer-card{
  margin-top: clamp(2rem, 4vw, 3rem);
  padding: 1.35rem;
  border: 1px solid rgba(255, 255, 255, 0.11);
  border-radius: $public-radius-large;
  background: $public-surface-raised;
}

.offer-price{
  padding-bottom: 1.15rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.09);
}

.fromPrice{
  display: flex;
  flex-wrap: wrap;
  gap: 0.45rem 0.8rem;
  margin-bottom: 0.4rem;
  font-size: 0.8rem;

  .currency{
    color: rgb(168 162 158);
    text-decoration: line-through;
  }

  .percent{
    color: #E39737;
    font-weight: 700;
  }
}

.price-label{
  margin: 0 0 0.15rem;
  color: rgb(168 162 158);
  font-size: 0.78rem;
  font-weight: 650;
}

.price{
  font-size: clamp(1.7rem, 3vw, 2.35rem);
  font-weight: 700;
  letter-spacing: -0.025em;
}

.add-to-cart{
  display: grid;
  grid-template-columns: auto minmax(0, 1fr);
  gap: 0.85rem;
  align-items: end;
  padding-top: 1.15rem;
}

.amount-field{
  display: grid;
  gap: 0.4rem;

  label{
    color: rgb(168 162 158);
    font-size: 0.76rem;
    font-weight: 650;
  }

  :deep(.p-inputnumber-input){
    width: 62px;
    text-align: center;
  }
}

.request-button{
  min-height: 48px;
  border-color: #E39737;
  color: #151515;
  background: #E39737;
  font-weight: 750;

  &:hover{
    border-color: #f0ad58 !important;
    background: #f0ad58 !important;
  }
}

.request-benefits{
  display: grid;
  gap: 0.7rem;
  margin: 1.25rem 0 0;
  padding: 0;
  list-style: none;
  color: rgb(214 211 209);
  font-size: 0.86rem;

  li{
    display: flex;
    gap: 0.7rem;
    align-items: center;
  }

  i{
    width: 1rem;
    color: #E39737;
    font-size: 0.82rem;
    text-align: center;
  }
}

.category-disclosure{
  margin-top: 1.35rem;
  border-top: 1px solid rgba(255, 255, 255, 0.1);

  summary{
    display: flex;
    gap: 1rem;
    align-items: center;
    justify-content: space-between;
    padding: 1rem 0;
    color: rgb(214 211 209);
    cursor: pointer;
    font-size: 0.86rem;
    font-weight: 680;
    list-style: none;

    &::-webkit-details-marker{
      display: none;
    }

    > span{
      display: flex;
      gap: 0.7rem;
      align-items: center;
    }

    > span i{
      color: #E39737;
    }

    &:hover{
      color: white;
    }

    &:focus-visible{
      border-radius: 6px;
      outline: 2px solid #E39737;
      outline-offset: 3px;
    }
  }

  &[open] .disclosure-chevron{
    transform: rotate(180deg);
  }
}

.disclosure-chevron{
  color: rgb(168 162 158);
  font-size: 0.75rem;
  transition: transform 180ms ease;
}

.category-tree-content{
  padding: 0.25rem 0 1rem;
  border-top: 1px solid rgba(255, 255, 255, 0.07);

  :deep(.menuContainer){
    max-width: none;
    margin: 0;
    padding: 0.75rem 0 0;
  }

  :deep(.p-panelmenu-panel){
    border-bottom: 1px solid rgba(255, 255, 255, 0.06);
  }

  :deep(.p-panelmenu-header-link),
  :deep(.p-panelmenu-item-link){
    padding-block: 0.7rem;
    font-size: 0.84rem;
  }
}

.details{
  overflow: hidden;
  border: 1px solid rgba(255, 255, 255, 0.09);
  border-radius: $public-radius-large;
  background: rgba(27, 27, 29, 0.65);

  :deep(.p-accordionpanel){
    border-color: rgba(255, 255, 255, 0.09);
  }

  :deep(.p-accordionheader),
  :deep(.p-accordioncontent-content){
    color: white;
    background: transparent;
  }

  :deep(.p-accordionheader){
    padding: 1.25rem 1.4rem;
    font-size: 1.05rem;
  }

  :deep(.p-accordioncontent-content){
    padding: 0 1.4rem 1.5rem;
    color: rgb(214 211 209);
    line-height: 1.7;
  }

  :deep(table){
    width: 100%;
    margin-bottom: 1.25rem;
    text-align: left;

    th{
      font-weight: 700;
    }

    td, th{
      padding: 0.65rem 0.3rem;
      border-bottom: 1px solid rgba(255, 255, 255, 0.1);
    }
  }

  :deep(h2), :deep(h3), :deep(h4){
    margin: 1.2rem 0 0.65rem;
    color: white;
    font-weight: 720;
  }

  :deep(h2){ font-size: 1.35rem !important; }
  :deep(h3){ font-size: 1.15rem !important; }
  :deep(h4){ font-size: 1rem !important; }

  :deep(p){
    width: 100%;
    margin-bottom: 0.7rem;

    &:empty{
      display: block;
      min-height: 1em;
    }
  }

  :deep(ul){
    padding-left: 1.4rem;
    list-style: circle;
  }
}

.product-consultation{
  display: flex;
  gap: 2rem;
  align-items: center;
  justify-content: space-between;
  margin-top: clamp(2.75rem, 5vw, 4.5rem);
  padding: clamp(1.5rem, 4vw, 2.5rem);
  border: 1px solid $public-border-strong;
  border-radius: $public-radius-large;
  background: $public-surface;

  > div{
    max-width: 690px;
  }

  h2{
    margin: 0;
    font-size: clamp(1.65rem, 3vw, 2.5rem);
    font-weight: 600;
    line-height: 1.1;
    letter-spacing: -0.03em;
  }

  p:last-child{
    margin: 0.8rem 0 0;
    color: rgb(191 188 185);
    line-height: 1.6;
  }

  :deep(.p-button){
    flex: 0 0 auto;
  }
}

.related-products{
  margin-top: clamp(2.75rem, 5vw, 4.5rem);
}

.related-heading{
  margin-bottom: 1.3rem;

  h2{
    margin: 0;
    font-size: clamp(1.8rem, 3vw, 2.5rem);
    font-weight: 600;
  }
}

.related-products__list{
  display: flex;
  gap: 1.25rem;
  overflow-x: auto;
  padding-bottom: 1rem;
}

.product-skeleton{
  min-height: 65vh;
  padding-top: 2rem;
}

@media (max-width: 860px){
  .product-hero{
    grid-template-columns: 1fr;
    gap: 2rem;
  }

  .product-summary{
    padding-top: 0;
  }

  .name{
    max-width: none;
  }
}

@media (max-width: 639px){
  .product-detail-shell{
    padding: 0 1rem;
  }

  .product-breadcrumb{
    padding-block: 1rem;
  }

  .product-hero{
    padding-top: 0.35rem;
  }

  .product-gallery{
    border-radius: $public-radius-large;

    .product-image{
      height: min(76vw, 320px);
      padding: 1rem;

      &.product-image--cover{
        height: auto;
        padding: 0;
      }
    }
  }

  .name{
    font-size: clamp(2.15rem, 10vw, 2.85rem);
  }

  .offer-card{
    margin-top: 1.35rem;
    padding: 1rem;
    border-radius: $public-radius-card;
  }

  .add-to-cart{
    grid-template-columns: 104px minmax(0, 1fr);
  }

  .request-button{
    width: 100%;
  }

  .product-consultation{
    align-items: stretch;
    flex-direction: column;

    :deep(.p-button){
      width: 100%;
    }
  }
}
</style>
<style lang="scss">

.p-image-mask{
  --maskbg: rgba(145, 145, 145, 0.9);
}
</style>
